import styled from "styled-components";
import { DATADESC } from "../../constants/app";
import SchemaDiagram from "./SchemaDiagram";

const PREPRINT =
  "https://www.sciencedirect.com/science/article/pii/S2666389924002149";
const JSON_SCHEMA = "https://datadesc.org/schema/datadesc_technical_schema.json";
const JSONLD_CONTEXT = "https://datadesc.org/schema/datadesc.jsonld";
const ERSMETA = "https://github.com/NFDI4Energy/ERSmeta";
const OPENAPI = "https://spec.openapis.org/oas/latest.html";
const JSON_SCHEMA_ORG = "https://json-schema.org/";
const SCHEMA_ORG = "https://schema.org/";
const CODEMETA = "https://codemeta.github.io/";
const QUDT = "https://qudt.org/";
const FNO = "https://w3id.org/function/ontology";

const Schema = () => {
  return (
    <Page>
      <Title>DataDesc Schema</Title>
      <Lead>
        A language-agnostic metadata schema for describing research software
        interfaces in a machine-actionable form.
      </Lead>

      <p>
        DataDesc records the functions of an interface and the data they
        consume or produce: types, formats, nested structures, units, and
        value ranges. That information is often only available as free-text
        documentation. Capturing it as structured metadata lets people and
        programs find a software, understand what it can do, and combine it
        with other tools and datasets without reading the source code.
      </p>
      <p>
        The schema is modelled on the{" "}
        <a href={OPENAPI} target="_blank" rel="noreferrer">
          OpenAPI Specification</a>, which standardizes descriptions of HTTP APIs. Research software is
        more often a locally installed program than a web service, so DataDesc
        keeps OpenAPI’s hierarchical, code-near structure and{" "}
        <a href={JSON_SCHEMA_ORG} target="_blank" rel="noreferrer">
          JSON Schema
        </a>{" "}
        type system, but describes permissible use of local interfaces rather
        than HTTP requests. General software metadata follows{" "}
        <a href={CODEMETA} target="_blank" rel="noreferrer">
          CodeMeta
        </a>{" "}
        and{" "}
        <a href={SCHEMA_ORG} target="_blank" rel="noreferrer">
          Schema.org</a>; units of measure follow{" "}
        <a href={QUDT} target="_blank" rel="noreferrer">
          QUDT</a>. DataDesc defines only the terms these languages do not already
        provide.
      </p>

      <Section>A DataDesc document</Section>
      <p>
        A complete DataDesc document has two parts.{" "}
        <a href="/terms/generalSoftwareInformation">
          General software information
        </a>{" "}
        — authors, license, contact, and related scholarly metadata — is an{" "}
        <a href={ERSMETA} target="_blank" rel="noreferrer">
          ERSmeta
        </a>{" "}
        description of type EnergyResearchSoftware.{" "}
        <a href="/terms/technicalInterfaceInformation">
          Technical interface information
        </a>{" "}
        is an{" "}
        <a href="/terms/API">API</a>: the functions, classes, and data of the
        software, each typed by a data schema.
      </p>
      <p>
        The annotation tools produce these parts separately. Merge combines
        them into one JSON-LD object of type{" "}
        <a href="/terms/DataDesc">DataDesc</a>, with a shared{" "}
        <code>@context</code>.
      </p>

      <Section>Structure</Section>
      <SchemaDiagram />

      <p>
        The technical root is an <a href="/terms/API">API</a>. It names the
        DataDesc and OpenAPI versions it follows, optional{" "}
        <a href="/terms/Info">Info</a> about the document, and{" "}
        <a href="/terms/ExternalDocumentation">external documentation</a>.
      </p>
      <p>
        An API exposes <a href="/terms/Function">functions</a> and, where the
        software is object-oriented, <a href="/terms/APIClass">API classes</a>{" "}
        with their own methods and class variables. Functions and APIs may
        also declare module-level input, output, and API{" "}
        <a href="/terms/Data">data</a>. Empty input or output lists are
        allowed.
      </p>
      <p>
        Every Data item has a <a href="/terms/DataSchema">data schema</a>: the
        JSON Schema type (<code>string</code>, <code>number</code>,{" "}
        <code>integer</code>, <code>boolean</code>, <code>array</code>,{" "}
        <code>object</code>, or <code>null</code>) plus constraints, units,
        and semantics. Arrays nest a further data schema for the element type.
        Objects list their fields as Data items under{" "}
        <a href="/terms/properties">properties</a>. Multi-dimensional values
        declare named <a href="/terms/Dimension">dimensions</a> with index
        bounds, rather than treating axes as stand-alone variables.
      </p>
      <p>
        Identifiers, expected types, and definitions for every term are on the{" "}
        <a href="/terms/">vocabulary</a> pages.
      </p>

      <Section>Linked vocabularies</Section>
      <p>
        DataDesc reuses existing terms where they already fit, so a document
        can be read as JSON Schema, OpenAPI, Schema.org, or JSON-LD without a
        private mapping layer. Borrowed terms resolve to their original
        vocabularies; DataDesc-owned terms live at{" "}
        <code>https://datadesc.org/terms/…</code>.
      </p>
      <List>
        <li>
          <a href={OPENAPI} target="_blank" rel="noreferrer">
            OpenAPI Specification
          </a>
          {" — "}
          document layout, operations, and external documentation
        </li>
        <li>
          <a href={JSON_SCHEMA_ORG} target="_blank" rel="noreferrer">
            JSON Schema
          </a>
          {" — "}
          types, formats, and validation keywords
        </li>
        <li>
          <a href={SCHEMA_ORG} target="_blank" rel="noreferrer">
            Schema.org
          </a>
          {" / "}
          <a href={CODEMETA} target="_blank" rel="noreferrer">
            CodeMeta
          </a>
          {" — "}
          scholarly and software metadata (via ERSmeta)
        </li>
        <li>
          <a href={QUDT} target="_blank" rel="noreferrer">
            QUDT
          </a>
          {" — "}
          units of measure and quantity kinds
        </li>
        <li>
          <a href={FNO} target="_blank" rel="noreferrer">
            Function Ontology
          </a>
          {" — "}
          functions and their inputs and outputs
        </li>
        <li>
          <a href={ERSMETA} target="_blank" rel="noreferrer">
            ERSmeta
          </a>
          {" — "}
          general description of energy research software
        </li>
      </List>

      <Section>Machine-readable sources</Section>
      <List>
        <li>
          {`JSON Schema (v${DATADESC.schema.version}):`}{" "}
          <a href={JSON_SCHEMA}>{JSON_SCHEMA}</a>
        </li>
        <li>
          JSON-LD context: <a href={JSONLD_CONTEXT}>{JSONLD_CONTEXT}</a>
        </li>
        <li>
          Vocabulary: <a href="/terms/">https://datadesc.org/terms/</a>
        </li>
      </List>

      <Section>Publication</Section>
      <p>
        The aims and design of DataDesc are described in the{" "}
        <a href={PREPRINT} target="_blank" rel="noreferrer">
          Patterns article
        </a>
        . Source files and tools are on{" "}
        <a href={`${DATADESC.github}/tree/main`} target="_blank" rel="noreferrer">
          GitHub
        </a>
        . DataDesc is released under the MIT License.
      </p>
    </Page>
  );
};

const Page = styled.div`
  width: 100%;
  max-width: 58rem;
  margin: 0 auto;
  color: var(--color-primary-dark);

  p {
    font-size: var(--size-smaller);
    font-weight: 300;
    line-height: 1.75;
    margin: 0 0 1rem;
  }

  a {
    color: var(--color-dark-green);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  code {
    font-size: 0.9em;
    background: var(--color-primary-light);
    padding: 0.05em 0.35em;
    border-radius: 4px;
  }
`;

const Title = styled.h1`
  && {
    font-size: clamp(1.75rem, 3vw, 2.25rem);
    font-weight: 500;
    letter-spacing: -0.03em;
    margin: 0 0 0.35em;
    color: var(--color-primary-dark);
  }
`;

const Section = styled.h2`
  && {
    font-size: 1.15rem;
    font-weight: 500;
    color: var(--color-primary-dark);
    letter-spacing: -0.02em;
    margin: 2.25rem 0 0.7rem;
  }
`;

const Lead = styled.p`
  font-size: 1.15rem !important;
  font-weight: 300;
  color: var(--color-dark-green);
  line-height: 1.5;
  margin: 0 0 1.75rem !important;
`;

const List = styled.ul`
  font-size: var(--size-smaller);
  font-weight: 300;
  line-height: 1.75;
  margin: 0 0 1rem;
  padding: 0;
  list-style: none;

  li {
    padding: 0.2rem 0 0.2rem 1rem;
    position: relative;
  }

  li::before {
    content: "·";
    position: absolute;
    left: 0;
    color: var(--color-dark-green);
  }

  a {
    color: var(--color-dark-green);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

export default Schema;
